import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@apollo/client/react';
import { STAFF_CONNECTION_AUDIT } from '@staff-app/graphql-client';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@staff-app/ui';
import { formatDateTimeWithSeconds } from '@/lib/format-date';
import { History } from 'lucide-react';

interface ConnectionAuditRow {
  ts: string;
  region: string;
  proto: string;
  service: string;
  srcIp: string;
  dstIp: string;
  dstPort: number;
  result: string;
  durationMs?: number | null;
  bytes?: number | null;
  srcSubnet?: string | null;
  dstSubnet?: string | null;
  srcEmail?: string | null;
  srcName?: string | null;
  dstDevice?: string | null;
  dstName?: string | null;
}

interface AuditResult {
  staffConnectionAudit: { rows: ConnectionAuditRow[]; hasMore: boolean };
}

const PAGE_SIZE = 100;

function fmtDuration(ms?: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * One label/value pair (a `<dt>/<dd>`) inside the Connection Audit detail dialog's `<dl>`.
 * @param mono render the value in a monospace font (for IPs / subnets).
 */
function Field({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className={mono ? 'font-mono text-foreground' : 'text-foreground'}>{value}</dd>
    </div>
  );
}

export function ConnectionAuditPage() {
  // Draft filter inputs (committed to `applied` on Apply, so we don't query per keystroke).
  const [srcEmail, setSrcEmail] = useState('');
  const [dstDevice, setDstDevice] = useState('');
  const [service, setService] = useState('all');
  const [result, setResult] = useState('all');
  const [applied, setApplied] = useState<Record<string, string>>({});
  const [offset, setOffset] = useState(0);
  const [viewing, setViewing] = useState<ConnectionAuditRow | null>(null);

  const filter = useMemo(
    () => ({ ...applied, offset, limit: PAGE_SIZE }),
    [applied, offset],
  );

  const { data, loading, error } = useQuery<AuditResult>(STAFF_CONNECTION_AUDIT, {
    variables: { filter },
    fetchPolicy: 'cache-and-network',
  });

  const rows = data?.staffConnectionAudit.rows ?? [];
  const hasMore = data?.staffConnectionAudit.hasMore ?? false;
  // Query returns newest-first; display as-is → newest at top, oldest at bottom.
  const displayRows = rows;

  function apply() {
    const next: Record<string, string> = {};
    if (srcEmail.trim()) next.srcEmail = srcEmail.trim();
    if (dstDevice.trim()) next.dstDevice = dstDevice.trim();
    if (service !== 'all') next.service = service;
    if (result !== 'all') next.result = result;
    setOffset(0);
    setApplied(next);
  }

  return (
    <div className="flex flex-col gap-6 p-6" data-testid="connection-audit-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
          <History className="h-6 w-6" />
          {'Connection Audit'}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {'Network-truth log of staff→device reaches (ssh/adb/vnc) captured at the VPN gateway. Source: wg-orchestrator connection_audit (PostgreSQL, append-only). Read-only.'}
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">{'Person (email)'}</label>
          <Input
            data-testid="audit-filter-email"
            placeholder="name@optisigns.com"
            value={srcEmail}
            onChange={(e) => setSrcEmail(e.target.value)}
            className="w-56"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">{'Device UUID'}</label>
          <Input
            data-testid="audit-filter-device"
            placeholder="device uuid"
            value={dstDevice}
            onChange={(e) => setDstDevice(e.target.value)}
            className="w-56"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">{'Service'}</label>
          <Select value={service} onValueChange={setService}>
            <SelectTrigger className="w-28" data-testid="audit-filter-service">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{'All'}</SelectItem>
              <SelectItem value="ssh">{'ssh'}</SelectItem>
              <SelectItem value="adb">{'adb'}</SelectItem>
              <SelectItem value="vnc">{'vnc'}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">{'Result'}</label>
          <Select value={result} onValueChange={setResult}>
            <SelectTrigger className="w-36" data-testid="audit-filter-result">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{'All'}</SelectItem>
              <SelectItem value="established">{'established'}</SelectItem>
              <SelectItem value="no-reply">{'no-reply'}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={apply} data-testid="audit-filter-apply">{'Apply'}</Button>
      </div>

      {error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {error.message}
        </div>
      ) : loading && !rows.length ? (
        <div className="text-sm text-muted-foreground">{'Loading audit log…'}</div>
      ) : (
        <>
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{'Time'}</TableHead>
                  <TableHead>{'Person'}</TableHead>
                  <TableHead>{'Device'}</TableHead>
                  <TableHead>{'Service'}</TableHead>
                  <TableHead>{'Result'}</TableHead>
                  <TableHead>{'Duration'}</TableHead>
                  <TableHead>{'Region'}</TableHead>
                  <TableHead className="text-right">{''}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayRows.map((r, i) => (
                  <TableRow key={`${r.ts}-${r.srcIp}-${r.dstIp}-${r.dstPort}-${i}`}>
                    <TableCell className="whitespace-nowrap text-sm">
                      {formatDateTimeWithSeconds(r.ts)}
                    </TableCell>
                    <TableCell>
                      <div>{r.srcEmail || r.srcName || <span className="text-muted-foreground">{'(unattributed)'}</span>}</div>
                      <div className="font-mono text-xs text-muted-foreground">{r.srcIp}</div>
                    </TableCell>
                    <TableCell>
                      <div>{r.dstName || r.dstDevice || <span className="text-muted-foreground">{'(unknown)'}</span>}</div>
                      <div className="font-mono text-xs text-muted-foreground">{r.dstIp}</div>
                    </TableCell>
                    <TableCell>{r.service}</TableCell>
                    <TableCell>
                      <Badge variant={r.result === 'established' ? 'default' : 'secondary'}>
                        {r.result}
                      </Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{fmtDuration(r.durationMs)}</TableCell>
                    <TableCell>{r.region}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setViewing(r)}
                        className="cursor-pointer"
                        data-testid={`audit-view-${r.ts}-${r.srcIp}-${r.dstIp}-${r.dstPort}-${i}`}
                      >
                        {'View'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!rows.length ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-sm text-muted-foreground">
                      {'No connection-audit rows match.'}
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>

          {/* Offset pagination */}
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            >
              {'← Newer'}
            </Button>
            <span className="text-sm text-muted-foreground">
              {`rows ${offset + 1}–${offset + rows.length}`}
            </span>
            <Button
              variant="outline"
              disabled={!hasMore}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
            >
              {'Older →'}
            </Button>
          </div>
        </>
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent data-testid="audit-detail">
          <DialogHeader>
            <DialogTitle>{viewing ? `${viewing.service} reach` : 'Connection detail'}</DialogTitle>
            <DialogDescription>
              {viewing ? formatDateTimeWithSeconds(viewing.ts) : ''}
            </DialogDescription>
          </DialogHeader>
          {viewing ? (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Field
                label="Result"
                value={
                  <Badge variant={viewing.result === 'established' ? 'default' : 'secondary'}>
                    {viewing.result}
                  </Badge>
                }
              />
              <Field
                label="Service / proto"
                value={`${viewing.service}${viewing.proto ? ` / ${viewing.proto}` : ''}`}
              />
              <Field label="Region" value={viewing.region} />
              <Field label="Duration" value={fmtDuration(viewing.durationMs)} />
              <Field label="Bytes" value={viewing.bytes ?? '—'} />
              <Field label="Person" value={viewing.srcEmail || viewing.srcName || '(unattributed)'} />
              <Field label="Source IP" value={viewing.srcIp} mono />
              <Field label="Source subnet" value={viewing.srcSubnet || '—'} mono />
              <Field label="Device" value={viewing.dstName || viewing.dstDevice || '(unknown)'} />
              <Field label="Dest IP" value={viewing.dstIp} mono />
              <Field label="Dest port" value={String(viewing.dstPort)} mono />
              <Field label="Dest subnet" value={viewing.dstSubnet || '—'} mono />
            </dl>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
